/**
 * React hook for health-facility fetching, selection options, and facility switching.
 */

import { useEffect, useMemo, useState } from "react";
import { HealthFacilityField } from "../enums/healthFacilityField";
import { FirestoreService } from "../firebase/firestoreService";
import { FirestoreCollectionReference } from "../firebase/references";
import {
  getFacilityRecoveryIds,
  getTokenFacilityInfo,
} from "../services/memberManagementService";
import { useAuthSelector } from "../state/AuthStoreProvider";
import type { SingleHealthFacilitySliceModel } from "../state/singleHealthFacilitySlice";
import type { AuthRootState } from "../state/store";
import {
  UserGeneralInfoContextKeys,
  UserStore,
} from "../store/userGeneralInfoStore";
import type {
  HealthFacility,
  InternalOrganization,
} from "../settings/profile/types";
import type { DataState, ProviderWithId } from "../types";
import { logError } from "../utils/customLogger";

/**
 * Manages health-facility fetching, selection options, and facility switching.
 *
 * @returns Facility options, current selections, loading state, and update handler
 */
export function useHealthFacilitySelection() {
  const { data: providerDetails } = useAuthSelector(
    (state: AuthRootState) => state.providerDetails,
  ) as DataState<ProviderWithId>;

  const { data: singleHealthFacilityDetails } = useAuthSelector(
    (state: AuthRootState) => state.singleHealthFacilityDetails,
  ) as SingleHealthFacilitySliceModel;

  const [healthFacilities, setHealthFacilities] = useState<HealthFacility[]>(
    [],
  );

  const [internalOrganizations, setInternalOrganizations] = useState<
    InternalOrganization[]
  >([]);

  const [fetchingHealthFacility, setFetchingHealthFacility] = useState(false);
  const [loading, setLoading] = useState(false);

  /**
   * Loads all healthcare facilities from Firestore.
   *
   * @returns Resolves when the fetch completes (errors are logged, not thrown)
   */
  const fetchHealthFacility = async () => {
    setFetchingHealthFacility(true);
    try {
      const healthFacilityData = (await FirestoreService.getAllDocuments(
        FirestoreCollectionReference.healthcareFacility(),
      )) as HealthFacility[];
      setHealthFacilities(healthFacilityData);
    } catch (error) {
      logError(error as Error, "Error fetching health facility data");
    } finally {
      setFetchingHealthFacility(false);
    }
  };

  /**
   * Returns whether the user's current facility is an external organization.
   */
  const checkIfExternalOrganization = (): boolean => {
    const currentFacilityId = UserStore.getItem(
      UserGeneralInfoContextKeys.HEALTHCARE_FACILITY_ID,
    );
    if (!currentFacilityId || healthFacilities.length === 0) return false;

    const currentFacility = healthFacilities.find(
      (facility) => facility.id === currentFacilityId,
    );

    return currentFacility?.is_external_organization === true;
  };

  /**
   * Resolves the internal organization linked to the current external facility.
   */
  const fetchInternalOrganizations = () => {
    const correspondingInternalOrgId =
      singleHealthFacilityDetails?.corresponding_internal_org_id;

    if (!correspondingInternalOrgId || healthFacilities.length === 0) {
      setInternalOrganizations([]);
      return;
    }

    const internalOrgFacility = healthFacilities.find(
      (facility) => facility.id === correspondingInternalOrgId,
    );

    if (internalOrgFacility) {
      const internalOrg: InternalOrganization = {
        facility_id: correspondingInternalOrgId,
        facility_name:
          internalOrgFacility.name || internalOrgFacility.Name || "",
      };
      setInternalOrganizations([internalOrg]);
    } else {
      setInternalOrganizations([]);
    }
  };

  /**
   * Updates the provider's facility and reloads the dashboard.
   *
   * @param facilityId - Target healthcare facility id
   */
  const updateFacilityId = async (facilityId: string) => {
    setLoading(true);
    try {
      await FirestoreService.updateDocument(
        FirestoreCollectionReference.providers(),
        providerDetails?.id || "",
        {
          FacilityId: facilityId,
        },
      );

      UserStore.clearAll();
      window.location.href = "/";
    } catch (error) {
      logError(error as Error, "Error updating facility id");
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchHealthFacility();
  }, []);

  useEffect(() => {
    if (healthFacilities.length > 0 && singleHealthFacilityDetails) {
      const isExternal = checkIfExternalOrganization();

      if (isExternal) {
        fetchInternalOrganizations();
      }
    }
  }, [healthFacilities, singleHealthFacilityDetails]);

  /**
   * Dropdown options filtered to facilities the current RBAC token may access.
   *
   * @returns Id/label pairs for the health-facility searchable select
   */
  const healthFacilityOptions = useMemo(() => {
    const tokenFacilityInfo = getTokenFacilityInfo();
    const internalFacilityIds = tokenFacilityInfo?.internal_facilities || [];
    const externalFacilityIds = tokenFacilityInfo?.external_facilities || [];

    const allAllowedFacilityIds = [
      ...internalFacilityIds,
      ...externalFacilityIds,
    ];

    const tokenFacilityIds =
      allAllowedFacilityIds.length > 0
        ? allAllowedFacilityIds
        : tokenFacilityInfo?.all_facilities || [];

    // Recovery path: when the token exchange failed the token info is empty, so
    // fall back to the persisted recovery ids so the user can still switch to a
    // working facility instead of seeing an empty dropdown.
    const allowedFacilityIds =
      tokenFacilityIds.length > 0 ? tokenFacilityIds : getFacilityRecoveryIds();

    const facilitiesToShow = healthFacilities.filter((facility) =>
      allowedFacilityIds.includes(facility[HealthFacilityField.ID]),
    );

    return facilitiesToShow.map((healthFacility) => ({
      id: healthFacility[HealthFacilityField.ID],
      text:
        healthFacility[HealthFacilityField.NAME] ?? healthFacility.name ?? "",
    }));
  }, [healthFacilities]);

  /**
   * Returns the provider's currently assigned health facility document.
   */
  const getCurrentHealthFacility = (): HealthFacility | null => {
    if (providerDetails?.FacilityId && healthFacilities.length > 0) {
      return (
        healthFacilities.find(
          (facility) =>
            facility[HealthFacilityField.ID] === providerDetails.FacilityId,
        ) ?? null
      );
    }
    return null;
  };

  /**
   * Returns the internal organization currently selected for the user.
   */
  const getCurrentInternalOrganization = (): InternalOrganization | null => {
    const selectedInternalOrgId = UserStore.getItem(
      UserGeneralInfoContextKeys.INTERNAL_ORGANIZATION_FACILITY_ID,
    );
    if (selectedInternalOrgId && internalOrganizations.length > 0) {
      return (
        internalOrganizations.find(
          (org) => org.facility_id === selectedInternalOrgId,
        ) ?? null
      );
    }
    return null;
  };

  const currentHealthFacility = getCurrentHealthFacility();
  const currentInternalOrganization = getCurrentInternalOrganization();

  return {
    healthFacilityOptions,
    currentHealthFacility,
    currentInternalOrganization,
    fetchingHealthFacility,
    loading,
    updateFacilityId,
  };
}
